import { useState } from "react";
import axios from "axios";
import Reveal from "./Reveal";
import { Phone, Send, Loader2 } from "lucide-react";
import { CONTACT_INFO, SERVICE_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="text-sm font-bold uppercase tracking-wider text-primary">Get In Touch</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-foreground">
            Ready to <span className="text-primary">Get Started?</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base md:text-lg">
            Request a quote, ask a question, or schedule a pickup. We respond fast — just like our deliveries.
          </p>
        </Reveal>

        <div className="mt-12 md:mt-16 grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <Reveal className="lg:col-span-2 space-y-4">
            {CONTACT_INFO.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="flex items-center gap-4 rounded-2xl border border-border bg-white p-4">
                  <span className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
                    <Icon className="w-5 h-5 text-primary" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{c.label}</div>
                    <div className="text-base font-bold text-foreground break-words">{c.value}</div>
                  </div>
                </div>
              );
            })}
            <div className="rounded-2xl bg-gradient-to-br from-primary to-blue-700 p-6 text-white">
              <h3 className="font-bold text-lg mb-2">Need It Today?</h3>
              <p className="text-sm text-blue-50/90 mb-4">
                Call our express line for same-day delivery. We can have a driver at your door within 30 minutes.
              </p>
              <a href="tel:+18882714255" className="inline-flex items-center gap-2 text-lg font-bold">
                <Phone className="w-5 h-5" /> (888) 271-4255
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-border bg-white p-6 md:p-8 shadow-lg shadow-primary/5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name">
                  <input data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="John Smith" className={inputCls} />
                </Field>
                <Field label="Email Address" required>
                  <input data-testid="contact-email" type="email" required value={form.email} onChange={update("email")} placeholder="john@company.com" className={inputCls} />
                </Field>
                <Field label="Phone Number">
                  <input data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="+1 (555) 000-0000" className={inputCls} />
                </Field>
                <Field label="Service Needed">
                  <select data-testid="contact-service" value={form.service} onChange={update("service")} className={inputCls}>
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Tell Us About Your Delivery">
                  <textarea data-testid="contact-message" rows={4} value={form.message} onChange={update("message")} placeholder="Describe what you need delivered, pickup/drop-off locations, and any special requirements..." className={`${inputCls} resize-none`} />
                </Field>
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === "sending"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 disabled:opacity-70 transition-all"
              >
                {status === "sending" ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</> : <>Send Message <Send className="w-4 h-4" /></>}
              </button>

              {status === "success" && (
                <p data-testid="contact-success" className="mt-4 text-center text-sm font-semibold text-green-600">
                  Thanks! Your message has been sent — we'll be in touch shortly.
                </p>
              )}
              {status === "error" && (
                <p data-testid="contact-error" className="mt-4 text-center text-sm font-semibold text-red-600">
                  Something went wrong. Please call us at (888) 271-4255.
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-foreground mb-1.5">
      {label} {required && <span className="text-primary">*</span>}
    </span>
    {children}
  </label>
);

export default Contact;
